import { useEffect, useMemo, useRef } from "react";
import { CloseCircleOutlined, NodeIndexOutlined } from "@ant-design/icons";
import { setAiHighlightedDataObjects } from "store/dataObjects/actions";
import { findSimilarRecords } from "./match";
import type { ToolPartProps } from "./types";

interface FindSimilarInput {
    referenceDataObjectId?: string;
    referenceValues?: Record<string, unknown>;
    fields: string[];
    minScore?: number;
    description: string;
}

interface FindSimilarOutput {
    matchCount: number;
    matches: Array<{ dataObjectId: string; score: number }>;
    description: string;
}

export default function FindSimilarDataObjectsTool({ toolCallId, state, input, output, errorText, helpers }: ToolPartProps<FindSimilarInput, FindSimilarOutput>) {
    const { addToolOutput, dispatch, dataObjects, schemaFields } = helpers;
    const resolvedRef = useRef(false);

    const computed = useMemo(() => {
        if (!input || !Array.isArray(input.fields) || input.fields.length === 0) return null;
        let reference: Record<string, unknown> | null = input.referenceValues || null;
        if (!reference && input.referenceDataObjectId) {
            const ref = dataObjects.find((d) => d.dataObjectId === input.referenceDataObjectId);
            if (ref) {
                reference = {};
                for (const fieldName of input.fields) {
                    const sf = schemaFields.find((f) => (f?.name || "").toLowerCase() === fieldName.toLowerCase());
                    const key = sf?.fieldId || fieldName;
                    (reference as any)[fieldName] = (ref as any)[key];
                }
            }
        }
        if (!reference) return null;
        const minScore = typeof input.minScore === "number" ? input.minScore : 0.6;
        return findSimilarRecords(dataObjects, schemaFields, reference, input.fields, minScore, input.referenceDataObjectId);
    }, [input, dataObjects, schemaFields]);

    useEffect(() => {
        if (resolvedRef.current) return;
        if (state !== "input-available") return;
        resolvedRef.current = true;
        if (!computed) {
            addToolOutput({
                tool: "findSimilarDataObjects",
                toolCallId,
                output: { matchCount: 0, matches: [], description: input?.description || "" },
            });
            dispatch(setAiHighlightedDataObjects([]));
            return;
        }
        const ids = computed.map((m) => m.dataObjectId);
        dispatch(setAiHighlightedDataObjects(ids));
        addToolOutput({
            tool: "findSimilarDataObjects",
            toolCallId,
            output: {
                matchCount: ids.length,
                matches: computed,
                description: input?.description || "",
            },
        });
    }, [state, computed, dispatch, addToolOutput, toolCallId, input]);

    const matches = state === "output-available" && output ? output.matches : computed || [];
    const matchCount = state === "output-available" && output ? output.matchCount : matches.length;
    const description = (state === "output-available" && output?.description) || input?.description || "";

    return (
        <div className="rounded-2xl border border-amber-200 bg-white p-3 text-sm text-zinc-800 shadow-sm">
            <div className="mb-1 flex items-center gap-2 text-amber-700">
                <NodeIndexOutlined />
                <span className="text-xs font-semibold uppercase tracking-wider">Similar / near-duplicate records</span>
            </div>
            {description && <div className="text-xs text-zinc-600">{description}</div>}
            <div className="mt-1 text-xs text-zinc-500">
                {matchCount === 0 ? "No similar records found." : `${matchCount} similar record${matchCount === 1 ? "" : "s"} highlighted in the list.`}
            </div>
            {matches.length > 0 && matches.length <= 10 && (
                <ul className="mt-2 space-y-1 text-[10px] text-zinc-500">
                    {matches.map((m) => (
                        <li key={m.dataObjectId}>
                            <span className="font-mono text-zinc-700">{m.dataObjectId}</span> · score {(m.score * 100).toFixed(0)}%
                        </li>
                    ))}
                </ul>
            )}
            {state === "output-error" && (
                <div className="mt-2 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">
                    <CloseCircleOutlined /> {errorText || "Tool error."}
                </div>
            )}
        </div>
    );
}
